import React, { useEffect, useState } from "react"
import { useDispatch, useSelector } from "react-redux"
import { Container } from "react-bootstrap"
import { Grid, InputAdornment, Typography, useTheme } from "@mui/material"
import { Masonry } from "@mui/lab"
import { Formik } from "formik"

import Header from "components/Header/Header"
import AppButton from "components/StyledComponents/AppButton"
import CustomNavBar from "../components/CustomNavBar"
import SubCategoryNavBar from "../components/CustomSubCategoryNavBar"
import CategoryContainerView from "../components/CategoryContainerView"
import ServiceCategoryModal from "../components/ServiceCategoryModal"

import { createBreadcrumbs } from "helpers/breadCrumbs"
import { validationSchemaCategoryModal } from "helpers/validationSchema"
import styles from "../hotel.module.scss"
import PlusIcon from "assets/icons/Plus.svg"
import EditIcon from "assets/icons/edit.svg"

import { hotelAction } from "redux/hotel/slice/user"
import {
  createHotelNestedSubCategoryAction,
  createHotelSubCategoryAction,
  editHotelNestedSubCategoryAction,
  getDynamicFieldsAction,
  getHotelNestedCategoryAction,
  getHotelServiceItemAction,
  getHotelSubCategoryAction
} from "redux/hotel/action"
import SearchTextField from "components/StyledComponents/SearchTextField"
import SearchIcon from "@mui/icons-material/Search"
import { useDebounce } from "hooks/utils"
import { searchHotelServices } from "helpers/functions"

const HotelCategory = () => {
  const theme = useTheme()
  const dispatch = useDispatch()
  const [filteredItems, setFilteredItems] = useState([])

  const {
    hotelService,
    linkCategory,
    hotelSubCategoryList,
    hotelNestedCategoryList,
    linkNestedCategory,
    hotelServiceItemList
  } = useSelector((state) => state.hotel)

  const [breadCrumbs, setBreadcrumbs] = useState([])
  const [isEdit, setIsEdit] = useState(false)
  const [categoryModal, setCategoryModal] = useState(false)
  const [modalType, setModalType] = useState("")
  const [currentCategoryData, setCurrentCategoryData] = useState({})
  const [isEditItem, setIsEditItem] = useState(false)
  const [searchValue, setSearchValue] = useState("")
  const debouncedSearchTerm = useDebounce(searchValue, 500)

  const sharedButtonStyle = {
    backgroundColor: theme.palette.background.main,
    padding: "10px 20px"
  }

  // --- Effects ---
  useEffect(() => {
    if (hotelService?.id) {
      dispatch(getHotelSubCategoryAction({ id: hotelService.id }))
      document.title = hotelService.name || "Hotel"
    }
    setBreadcrumbs(createBreadcrumbs())
  }, [hotelService])

  useEffect(() => {
    if (linkCategory?.id) {
      dispatch(getHotelNestedCategoryAction({ id: linkCategory.id }))
    }
  }, [linkCategory?.id])

  useEffect(() => {
    if (linkNestedCategory?.id) {
      dispatch(getHotelServiceItemAction({ nestedId: linkNestedCategory.id }))
    } else {
      dispatch(hotelAction.setHotelServiceItemList([]))
    }
  }, [linkNestedCategory])

  useEffect(() => {
    if (hotelSubCategoryList?.length) {
      dispatch(hotelAction.setHotelCategoryLink(hotelSubCategoryList[0]))
    }
  }, [hotelSubCategoryList])

  useEffect(() => {
    if (hotelNestedCategoryList?.length) {
      dispatch(
        hotelAction.setHotelNestedCategoryLink({
          ...hotelNestedCategoryList[0],
          list: "Summary View"
        })
      )
    } else {
      dispatch(hotelAction.setHotelNestedCategoryLink({ list: "Summary View" }))
    }
  }, [hotelNestedCategoryList])

  // --- Handlers ---
  const toggleEdit = () => setIsEdit((prev) => !prev)

  const openModal = (type) => {
    setModalType(type)
    setCategoryModal(true)
  }

  const handleClose = () => {
    setCategoryModal(false)
  }

  const handleCategorySubmit = (values) => {
    const name = values?.category

    if (modalType === "category") {
      const payload = { name, hotel_config: hotelService?.id }
      dispatch(createHotelSubCategoryAction({ payload, onClose: handleClose }))
    } else {
      const payload = { name, nested_subcategory: linkNestedCategory?.id }
      dispatch(createHotelNestedSubCategoryAction({ payload, onClose: handleClose }))
    }

    setIsEdit(false)
  }

  const handleCategoryEdit = (values) => {
    const payload = {
      name: values?.category,
      nested_subcategory_id: currentCategoryData?.nested_nested_subcategory_id
    }

    dispatch(
      editHotelNestedSubCategoryAction({
        payload,
        parentId: linkNestedCategory?.id,
        onClose: handleClose
      })
    )
  }

  const initialValues = {
    category: isEdit ? currentCategoryData?.nested_nested_subcategory_name || "" : ""
  }

  const handleSearchField = (e) => {
    setSearchValue(e.target.value)
  }

  useEffect(() => {
    const results = searchHotelServices(
      hotelServiceItemList?.nested_nested_subcategories,
      debouncedSearchTerm
    )
    setFilteredItems(results)
  }, [debouncedSearchTerm, hotelServiceItemList])

  useEffect(() => {
    if (linkCategory?.hotel_config) {
      dispatch(getDynamicFieldsAction(linkCategory?.hotel_config))
    }
  }, [linkCategory?.hotel_config])

  return (
    <div>
      <Formik
        initialValues={initialValues}
        enableReinitialize
        validationSchema={validationSchemaCategoryModal}
        onSubmit={isEdit ? handleCategoryEdit : handleCategorySubmit}>
        {(formik) => (
          <ServiceCategoryModal
            open={categoryModal}
            onClose={handleClose}
            isEdit={isEdit}
            formik={formik}
            title={modalType === "category" ? "Add New Category" : "Add New Sub Category"}
          />
        )}
      </Formik>

      <Grid container>
        <Grid item lg={12}>
          <Header title={hotelService?.name} breadCrumbs={breadCrumbs} />
        </Grid>

        <Grid item container lg={12} spacing={2} justifyContent="space-between">
          <Grid item md={6}>
            <CustomNavBar activeTab={linkCategory?.id} navBarList={hotelSubCategoryList} />
          </Grid>

          <Grid item md={6} container justifyContent="flex-end" spacing={2}>
            {!isEdit && (
              <Grid item>
                <AppButton onClick={() => openModal("category")} styles={sharedButtonStyle}>
                  <img src={PlusIcon} alt="add" className="flex-shrink-0 me-1" />
                  Add New Category
                </AppButton>
              </Grid>
            )}
            {hotelSubCategoryList?.length > 0 && (
              <Grid item>
                <AppButton
                  onClick={toggleEdit}
                  styles={{ ...sharedButtonStyle, padding: "10px 24px" }}>
                  {isEdit ? (
                    "Done"
                  ) : (
                    <>
                      <img
                        src={EditIcon}
                        alt="edit"
                        className={`flex-shrink-0 me-1 ${styles.editIcon}`}
                      />
                      Edit
                    </>
                  )}
                </AppButton>
              </Grid>
            )}
          </Grid>
        </Grid>

        {hotelSubCategoryList?.length > 0 ? (
          <>
            <Grid item xs={12} mt={6} mb={3}>
              <SubCategoryNavBar
                activeTab={linkNestedCategory?.id}
                isEdit={isEdit}
                navBarList={hotelNestedCategoryList}
              />
            </Grid>
            <Grid item xs={4}>
              <SearchTextField
                id="search"
                name="search"
                value={searchValue}
                onChange={handleSearchField}
                fullWidth
                type="search"
                placeholder={`Search ${linkCategory?.hotel_config_name}`}
                InputProps={{
                  disableUnderline: true,
                  endAdornment: (
                    <InputAdornment
                      position="end"
                      sx={{
                        backgroundColor: theme.palette.primary.main
                      }}>
                      <SearchIcon htmlColor={`${theme.palette.text.secondary}`} />
                    </InputAdornment>
                  )
                }}
                style={{
                  border: `1px solid ${theme.palette.text.secondary}`,
                  backgroundColor: theme.palette.primary.main,
                  borderRadius: 6
                }}
              />
            </Grid>
            {!isEdit && hotelNestedCategoryList?.length > 0 && (
              <Grid item xs={8} container justifyContent="flex-end">
                <AppButton onClick={() => openModal("sub_category")} styles={sharedButtonStyle}>
                  <img src={PlusIcon} alt="add" className="flex-shrink-0 me-1" />
                  Add New
                </AppButton>
              </Grid>
            )}

            <Container fluid className="p-0 mt-4">
              <Masonry columns={2} spacing={2}>
                {Array.isArray(filteredItems) && filteredItems?.length > 0 ? (
                  filteredItems?.map((categoryData, idx) => (
                    <CategoryContainerView
                      key={idx}
                      data={categoryData}
                      searchedText={debouncedSearchTerm}
                      isEdit={isEdit}
                      handleAddCategory={() => openModal("sub_category")}
                      setCurrentCategoryData={setCurrentCategoryData}
                      linkNestedCategory={linkNestedCategory}
                      isEditItem={isEditItem}
                      setIsEditItem={setIsEditItem}
                    />
                  ))
                ) : (
                  <Typography variant="h2">No Data Found</Typography>
                )}
              </Masonry>
            </Container>
          </>
        ) : (
          <Grid xs={12} className="d-flex justify-content-center mt-4">
            <Typography variant="h2">No Data Found</Typography>
          </Grid>
        )}
      </Grid>
    </div>
  )
}

export default HotelCategory
